import React, { createContext, useContext, useEffect, useState } from "react";
import { MFT_UPLOAD_AND_SAVE_DATA_API } from "../config/apiConfig";
import { securedFetch } from "../utils/api";
import { useUserContext } from "./UserContext";

const SFC_TYPES = {
  TCCI: "TCCI",
  TFA: "TFA",
  TMFNL: "TMFNL",
};

const accessMap = {
  [SFC_TYPES.TCCI]: [SFC_TYPES.TCCI],
  [SFC_TYPES.TFA]: [SFC_TYPES.TFA],
  [SFC_TYPES.TMFNL]: [SFC_TYPES.TMFNL],
};

const checkForAccess = (type, selectedUserDetail) => {
  if (!selectedUserDetail) return false;

  const {
    admin,
    applicationAdministrator,
    globalWrite,
    regions = [],
  } = selectedUserDetail || {};
  if (
    admin === true ||
    applicationAdministrator === true ||
    globalWrite === true
  ) {
    return true;
  }

  const allsfcs = regions?.flatMap((region) => region.sfcs || []);

  const allowedRoles = accessMap[type];
  if (!allowedRoles) {
    return false;
  }

  return allsfcs?.some(
    (sfc) =>
      allowedRoles.includes(sfc.businessEntityID) && sfc.writable === true,
  );
};

const DataUploadContext = createContext();

export const DataUploadProvider = ({ children }) => {
  const { loggedInUserEmail, selectedUserDetail } = useUserContext() || {};
  const [rows, setRows] = useState([]);

  const [allRows, setAllRows] = useState([
    {
      type: SFC_TYPES.TCCI,
      title: SFC_TYPES.TCCI,
      files: [],
      accept: ".csv",
      hasAccess: checkForAccess(SFC_TYPES.TCCI, selectedUserDetail),
      isMultiFileSupport: true,
      canUploadAndUpdate: true,
      canUploadAndReplace: false,
      uploading: false,
      isDownloadable: false,
      progress: 0,
      error: null,
    },
    {
      type: SFC_TYPES.TFA,
      title: SFC_TYPES.TFA,
      files: [],
      accept: ".csv",
      hasAccess: checkForAccess(SFC_TYPES.TFA, selectedUserDetail),
      isMultiFileSupport: true,
      canUploadAndUpdate: true,
      canUploadAndReplace: false,
      uploading: false,
      isDownloadable: false,
      progress: 0,
      error: null,
    },
    {
      type: SFC_TYPES.TMFNL,
      title: SFC_TYPES.TMFNL,
      files: [],
      accept: ".csv",
      hasAccess: checkForAccess(SFC_TYPES.TMFNL, selectedUserDetail),
      isMultiFileSupport: true,
      canUploadAndUpdate: true,
      canUploadAndReplace: false,
      uploading: false,
      isDownloadable: false,
      progress: 0,
      error: null,
    },
  ]);

  const [dialogState, setDialogState] = useState({
    open: false,
    title: "",
    message: "",
    successFiles: [],
    ignoredFiles: [],
    acceptedPrefixes: [],
  });

  const showDialog = ({
    title,
    message,
    successFiles = [],
    ignoredFiles = [],
    acceptedPrefixes = [],
  }) => {
    setDialogState({
      open: true,
      title,
      message,
      successFiles,
      ignoredFiles,
      acceptedPrefixes,
    });
  };

  const closeDialog = () => {
    setDialogState((prev) => ({
      ...prev,
      open: false,
    }));
  };

  useEffect(() => {
    const accessibleRows = allRows?.filter((row) => row.hasAccess === true);
    setRows(accessibleRows);
  }, [allRows, setAllRows]);

  const attachFiles = (rowIndex, files) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex].files = files;
      updated[rowIndex].progress = 0;
      updated[rowIndex].error = null;
      return updated;
    });
  };

  const removeFiles = (rowIndex) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex].files = [];
      updated[rowIndex].progress = 0;
      updated[rowIndex].error = null;
      return updated;
    });
  };

  /**
   * Upload file using securedFetch with optional progress tracking
   */
  const uploadFiles = async (rowIndex) => {
    const row = rows[rowIndex];
    if (!row.files || row.files.length === 0 || row.uploading) return;

    const formData = new FormData();

    row.files.forEach((file) => {
      formData.append("files", file);
    });

    formData.append("type", row.type);
    formData.append("uploadedBy", loggedInUserEmail || "System");

    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIndex
          ? { ...r, uploading: true, progress: 0, error: null }
          : r,
      ),
    );

    try {
      const response = await securedFetch(MFT_UPLOAD_AND_SAVE_DATA_API, {
        method: "POST",
        body: formData,
      });

      const data = await response.json().catch(() => null);

      if (response.ok) {
        showDialog({
          title: "Upload Result",
          message: data?.message || "Upload completed successfully.",
          successFiles: data?.successFiles || [],
          ignoredFiles: data?.ignoredFiles || [],
          acceptedPrefixes: data?.acceptedPrefixes || [],
        });

        setRows((prev) =>
          prev.map((r, i) =>
            i === rowIndex
              ? { ...r, uploading: false, progress: 100, files: [] }
              : r,
          ),
        );
      } else {
        showDialog({
          title: "Upload Failed",
          message: data?.message || "Upload failed.",
          successFiles: data?.successFiles || [],
          ignoredFiles: data?.ignoredFiles || [],
          acceptedPrefixes: data?.acceptedPrefixes || [],
        });

        setRows((prev) =>
          prev.map((r, i) =>
            i === rowIndex
              ? {
                  ...r,
                  uploading: false,
                  error: data?.message || "Upload failed",
                }
              : r,
          ),
        );
      }
    } catch (error) {
      console.error("Upload error:", error);

      showDialog({
        title: "Error",
        message: error.message || "Something went wrong during upload.",
      });

      setRows((prev) =>
        prev.map((r, i) =>
          i === rowIndex ? { ...r, uploading: false, error: error.message } : r,
        ),
      );
    }
  };

  return (
    <DataUploadContext.Provider
      value={{
        rows,
        attachFiles,
        removeFiles,
        uploadFiles,
        setRows,

        dialogState,
        closeDialog,
      }}
    >
      {children}
    </DataUploadContext.Provider>
  );
};

export const useDataUploadContext = () => useContext(DataUploadContext);
